import { RefObject, useEffect } from "react";

/**
 * Hook to listen for keydown events on a specific element
 * @param ref - React ref object pointing to the element
 * @param handler - Callback function to execute on keydown
 */
export function useKeydownListener<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  handler: (event: KeyboardEvent) => void,
): void {
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      const el = ref.current;

      // Only trigger if the event target is the element or its descendant
      if (!el || !(el === event.target || el.contains(event.target as Node))) {
        return;
      }

      handler(event);
    };

    const el = ref.current;
    if (el) {
      el.addEventListener("keydown", listener);
    }

    return () => {
      if (el) {
        el.removeEventListener("keydown", listener);
      }
    };
  }, [ref, handler]);
}
